import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";

import type { EnumLabel } from "../../../components/ui-kit/fields/utils";

import {
  AdverseEventLevel,
  AdverseEventType,
  DeathEventReason,
  EventCategory,
  HospitalizationEventReason,
  HospitalizationEventType,
  LabsEventType,
  PhysicianNotifiedEventType,
  VisitEventPlannedCategory,
  VisitEventType,
  VisitEventUnplannedCategory,
} from "../../../api";

export const eventCategory: Record<EventCategory, MessageDescriptor> = {
  [EventCategory.hospitalization]: msg`Hospitalization`,
  [EventCategory.death]: msg`Death`,
  [EventCategory.visit]: msg`Visit`,
  [EventCategory.medical_call]: msg`Medical call`,
  [EventCategory.technical_call]: msg`Technical call`,
  [EventCategory.medication_change]: msg`Medication change`,
  [EventCategory.labs]: msg`Labs`,
  [EventCategory.physician_notified]: msg`Treating physician notification`,
  [EventCategory.patient_non_adherence]: msg`Patient non-adherence`,
  [EventCategory.adverse_event]: msg`Adverse event`,
};

export type EventCategoryType =
  | HospitalizationEventType
  | DeathEventReason
  | VisitEventType
  | LabsEventType
  | PhysicianNotifiedEventType
  | AdverseEventType;

export const eventCategoryType: Record<EventCategoryType, MessageDescriptor> = {
  [HospitalizationEventType.unplanned]: msg`Unplanned`,
  [HospitalizationEventType.elective]: msg`Elective`,
  [DeathEventReason.hf]: msg`HF`,
  [DeathEventReason.cv]: msg`CV`,
  [DeathEventReason.non_cv]: msg`Non-CV`,
  [DeathEventReason.unknown]: msg`Unknown`,
  [VisitEventType.planned]: msg`Planned`,
  [LabsEventType.seerlinq]: msg`Triggered by Seerlinq`,
  [PhysicianNotifiedEventType.regular]: msg`Regular`,
  [AdverseEventType.hypokalemia]: msg`Hypokalemia`,
  [AdverseEventType.hyperkalemia]: msg`Hyperkalemia`,
  [AdverseEventType.hyponatremia]: msg`Hyponatremia`,
  [AdverseEventType.symptomatic_hypotension]: msg`Symptomatic hypotension`,
};

export type EventCategoryDetails =
  | HospitalizationEventReason
  | VisitEventUnplannedCategory
  | VisitEventPlannedCategory
  | AdverseEventLevel;

export const eventCategoryDetails: Record<
  EventCategoryDetails,
  MessageDescriptor
> = {
  [HospitalizationEventReason.hfh]: msg`HFH`,
  [HospitalizationEventReason.cv]: msg`CV`,
  [HospitalizationEventReason.non_cv]: msg`Non-CV`,
  [VisitEventUnplannedCategory.er_urgent_hf_iv_diuretics]: msg`ER-urgent HF visit with iv. diuretics admin.`,
  [VisitEventUnplannedCategory.outpatient]: msg`Outpatient`,
  [VisitEventUnplannedCategory.study_visit]: msg`Study visit`,
  [VisitEventUnplannedCategory.er_cv]: msg`ER - CV`,
  [VisitEventUnplannedCategory.er_non_cv]: msg`ER - Non-CV`,
  [VisitEventPlannedCategory.outpatient_seerlinq]: msg`Outpatient triggered by Seerlinq`,
  [VisitEventPlannedCategory.er_seerlinq]: msg`ER triggered by Seerlinq`,
  [AdverseEventLevel.mild]: msg`Mild`,
  [AdverseEventLevel.moderate]: msg`Moderate`,
  [AdverseEventLevel.severe]: msg`Severe`,
};

export function eventTypesEnum(
  arr: EventCategoryType[],
): Record<string, EnumLabel> {
  const res: Record<string, EnumLabel> = {};
  for (const item of arr) {
    res[item] = eventCategoryType[item];
  }

  return res;
}

export function eventDetailsEnum(
  arr: EventCategoryDetails[],
): Record<string, EnumLabel> {
  const res: Record<string, EnumLabel> = {};
  for (const item of arr) {
    res[item] = eventCategoryDetails[item];
  }
  return res;
}
